import { UniversalLoginApi } from './types';
import { Api } from '@apis/Api';
import { AppContextData } from '@contexts/app/AppContextData';
import { Logger } from '@services/logger/Logger';
import { Measurement } from '@services/measurement';
import { ResponseCode } from '@ul/api/lib/enums';
import {
    ApiHeader,
    BaseViewModel,
    ChangePasswordViewModel,
    DeleteAccountViewModel,
    ForgotPasswordEmailViewModel,
    ForgotPasswordPhoneViewModel,
    OAuthViewModel,
    OtpLoginViewModel,
    PasskeyAuthenticationOptionsResponse,
    SignInViewModel,
    SignupViewModel,
    UserPasskeysViewModel,
    UserPasskeyViewModel,
} from '@apis/model';
import {
    toChangePasswordViewModel,
    toForgotPasswordEmailViewModel,
    toForgotPasswordPhoneViewModel,
    toOAuthViewModel,
    toOtpLoginViewModel,
    toSignInViewModel,
    toSignupViewModel,
    toDeleteAccountViewModel,
    toGdprResponseCode,
    toUnifiedAuthViewModel,
    toForceLogoutViewModel,
    toUserPasskeysViewModel,
    toUserPasskeyViewModel,
    toBaseViewModel,
} from '@apis/responseTransformer';
import {
    AuthType,
    ChangePasswordRequest,
    ForgotPasswordEmailRequest,
    ForgotPasswordPhoneRequest,
    LoginRequest,
    LoginFlow,
    OAuthRequest,
    OtpLoginRequest,
    SignupRequest,
    UniversalLoginApiClient,
    GdprEraseStatus,
    SendOtpRequest,
    VerifyOtpRequest,
    PhoneAssociationRequest,
    WhatsappLinkRequest,
    UnifiedAuthRequest,
    SendAuthedOtpRequest,
    WalletResetPinRequest,
    WalletAuthRequest,
    WalletScaRequest,
    ScaInitRequest,
    ConsentInfoRequest,
    ConsentInfoResponse,
    SocialAssociationRequest,
    UserPasskeyUpdateFriendlyNameRequest,
    UserPasskeyDeleteRequest,
} from '@ul/api';
import { OtpChannelsViewModel } from '@apis/model/mfa/OtpChannelsViewModel';
import { toOtpChannelsViewModel } from '@apis/responseTransformer/toOtpChannelsViewModel';
import { OtpGenerateViewModel } from '@apis/model/mfa/OtpGenerateViewModel';
import { toOtpGenerateViewModel } from '@apis/responseTransformer/toOtpGenerateViewModel';
import { OtpVerifyViewModel } from '@apis/model/mfa/OtpVerifyViewModel';
import { toOtpVerifyViewModel } from '@apis/responseTransformer/toOtpVerifyViewModel';
import { toRefreshTokenViewModel } from '@apis/responseTransformer/toRefreshTokenViewModel';
import { RefreshTokenViewModel } from '@apis/model/partner/RefreshTokenViewModel';
import { PhoneAssociationViewModel } from '@apis/model/phoneAssociation/PhoneAssociationViewModel';
import { toPhoneAsscociationViewModel } from '@apis/responseTransformer/toPhoneAssociationViewModel';
import { toSocialAssociationViewModel } from '@apis/responseTransformer/toSocialAssociationViewModel';
import { SocialAssociationViewModel } from '@apis/model/socialAssociation/SocialAssociationViewModel';
import { SignInCallbackViewModel } from '@apis/model/signIn/SignInCallbackViewModel';
import { DeviceIntelligenceContext } from '@contexts/deviceIntelligence/DeviceIntelligenceContext';
import { CdnHeader } from '@apis/model/request/Header';
import { UnifiedAuthViewModel } from '@apis/model/unifiedAuth/UnifiedAuthViewModel';
import { WalletViewModel } from '@apis/model/wallet/WalletViewModel';
import { ScaInitViewModel } from '@apis/model/wallet/ScaInitViewModel';
import { toWalletViewModel } from '@apis/responseTransformer/toSetupPinViewModel';
import { ValidateTokenViewModel } from '@apis/model/validateToken/ValidateTokenViewModel';
import { PasskeyRegistrationOptionsResponse, RegisterPasskeyResponseViewModel } from '@apis/model';
import { RegisterPasskeyRequest } from './RegisterPasskeyRequest';
import { AuthenticateWithPasskeyRequest } from './AuthenticateWithPasskeyRequest';
import { PasskeyAuthenticateResponse } from '@ul/api';
import { FetchHandlerStatusCode } from '@ul/request';

export class UniversalLoginApiWrapper extends Api implements UniversalLoginApi {
    private readonly ulClient: UniversalLoginApiClient;
    private readonly deviceIntelligenceContext: DeviceIntelligenceContext;
    private isDeviceIntelligenceHeadersSent: boolean;

    constructor(
        appContext: AppContextData,
        measurement: Measurement,
        logger: Logger,
        ulClient: UniversalLoginApiClient,
        deviceIntelligenceContext: DeviceIntelligenceContext
    ) {
        super(appContext, measurement, logger, 'ul.clientside.api');
        this.ulClient = ulClient;
        this.deviceIntelligenceContext = deviceIntelligenceContext;
    }

    public signIn = async (signInRequest: LoginRequest): Promise<SignInViewModel> => {
        if (!this.deviceIntelligenceContext.isDeviceIntelligenceHeaderBuilt()) {
            this.deviceIntelligenceContext.buildDeviceIntelligenceHeaderAsync();
        }
        const shouldSendDeviceIntelligenceHeaders =
            this.deviceIntelligenceContext.isDeviceIntelligenceHeaderBuilt() && !this.isDeviceIntelligenceHeadersSent;
        const baseHeaders = this.buildUniversalLoginHeader();
        const headers = shouldSendDeviceIntelligenceHeaders
            ? { ...baseHeaders, ...this.deviceIntelligenceContext.getDeviceIntelligenceHeaders() }
            : baseHeaders;
        if (shouldSendDeviceIntelligenceHeaders) {
            this.isDeviceIntelligenceHeadersSent = true;
        }
        const isOnline = navigator.onLine;
        return this.measureP<SignInViewModel>(response => ({
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            success: response?.success ?? false,
            type: signInRequest.credentials.authType,
            endpoint: 'signIn',
        }))(
            this.ulClient.signIn(signInRequest, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('SignIn', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return toSignInViewModel(wrappedResponse.data);
            })
        );
    };

    public initialOtpLogin = () => {
        const headers = this.buildUniversalLoginHeader();
        const isOnline = navigator.onLine;
        return this.measureP<OtpLoginViewModel>(response => ({
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            endpoint: 'initialOtpLogin',
        }))(
            this.ulClient.initialOtpLogin(headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('InitialOtpLogin', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return toOtpLoginViewModel(wrappedResponse.data);
            })
        );
    };

    public otpLogin = (otpLoginRequest: OtpLoginRequest): Promise<OtpLoginViewModel> => {
        const headers = this.buildUniversalLoginHeader();
        const isOnline = navigator.onLine;
        return this.measureP<OtpLoginViewModel>(response => ({
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            success: response?.success ?? false,
            endpoint: 'otpLogin',
        }))(
            this.ulClient.otpLogin(otpLoginRequest, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('OtpLogin', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return toOtpLoginViewModel(wrappedResponse.data);
            })
        );
    };

    public oauth = (oauthRequest: OAuthRequest): Promise<OAuthViewModel> => {
        const baseHeaders = this.buildUniversalLoginHeader();
        const isOnline = navigator.onLine;

        const shouldSendDeviceIntelligenceHeaders =
            this.deviceIntelligenceContext.isDeviceIntelligenceHeaderBuilt() && !this.isDeviceIntelligenceHeadersSent;
        const headers = shouldSendDeviceIntelligenceHeaders
            ? { ...baseHeaders, ...this.deviceIntelligenceContext.getDeviceIntelligenceHeaders() }
            : baseHeaders;

        if (shouldSendDeviceIntelligenceHeaders) {
            this.isDeviceIntelligenceHeadersSent = true;
        }
        return this.measureP<OAuthViewModel>(response => ({
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            success: response?.success ?? false,
            userInfo: response?.userInfo
                ? {
                      firstName: response?.userInfo?.firstName,
                      lastName: response?.userInfo?.lastName,
                  }
                : undefined,
            type: oauthRequest.credentials.id,
            endpoint: 'oauth',
        }))(
            this.ulClient.oauth(oauthRequest, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('OAuth', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return toOAuthViewModel(wrappedResponse.data);
            })
        );
    };

    public initialForgotPassword = () => {
        const headers = this.buildUniversalLoginHeader();
        const isOnline = navigator.onLine;
        return this.measureP<ForgotPasswordEmailViewModel>(response => ({
            success: response?.success ?? false,
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            endpoint: 'initialForgotPassword',
        }))(
            this.ulClient.initialForgotPassword(headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('InitialForgotPasswordEmail', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return toForgotPasswordEmailViewModel(wrappedResponse.data);
            })
        );
    };

    public forgotPasswordEmail = (
        forgotPasswordRequest: ForgotPasswordEmailRequest
    ): Promise<ForgotPasswordEmailViewModel> => {
        const headers = this.buildUniversalLoginHeader();
        const isOnline = navigator.onLine;
        return this.measureP<ForgotPasswordEmailViewModel>(response => ({
            success: response?.success ?? false,
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            endpoint: 'forgotPasswordEmail',
        }))(
            this.ulClient.forgotPasswordEmail(forgotPasswordRequest, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('ForgotPasswordEmail', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return toForgotPasswordEmailViewModel(wrappedResponse.data);
            })
        );
    };

    public forgotPasswordPhone = (
        forgotPasswordRequest: ForgotPasswordPhoneRequest
    ): Promise<ForgotPasswordPhoneViewModel> => {
        const headers = this.buildUniversalLoginHeader();
        const isOnline = navigator.onLine;
        return this.measureP<ForgotPasswordPhoneViewModel>(response => ({
            success: response?.success ?? false,
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            endpoint: 'forgotPasswordPhone',
        }))(
            this.ulClient.forgotPasswordPhone(forgotPasswordRequest, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('ForgotPasswordPhone', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return toForgotPasswordPhoneViewModel(wrappedResponse.data);
            })
        );
    };

    public initialChangePassword = (authType: AuthType, token: string) => {
        const headers = {
            ...this.buildUniversalLoginHeader(),
            ...this.getTokenHeader(token),
        };
        const isOnline = navigator.onLine;
        return this.measureP<ChangePasswordViewModel>(response => ({
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            endpoint: 'initialChangePassword',
            type: authType,
        }))(
            this.ulClient.initialChangePassword(headers, { authType }).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('InitialChangePassword', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return toChangePasswordViewModel(wrappedResponse.data);
            })
        );
    };

    public changePassword = (
        changePasswordRequest: ChangePasswordRequest,
        token: string
    ): Promise<ChangePasswordViewModel> => {
        const headers = {
            ...this.buildUniversalLoginHeader(),
            ...this.getTokenHeader(token),
        };
        const isOnline = navigator.onLine;

        return this.measureP<ChangePasswordViewModel>(response => ({
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            success: response?.success ?? false,
            endpoint: 'changePassword',
        }))(
            this.ulClient.changePassword(changePasswordRequest, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('ChangePassword', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return toChangePasswordViewModel(wrappedResponse.data);
            })
        );
    };

    public initialSignup = () => {
        const headers = this.buildUniversalLoginHeader();
        const isOnline = navigator.onLine;
        return this.measureP<SignupViewModel>(response => ({
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            endpoint: 'initialSignup',
        }))(
            this.ulClient.initialSignup(headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('InitialSignup', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return toSignupViewModel(wrappedResponse.data);
            })
        );
    };

    public signup = async (signupRequest: SignupRequest): Promise<SignupViewModel> => {
        const headers = {
            ...this.buildUniversalLoginHeader(),
            ...(this.deviceIntelligenceContext.isDeviceIntelligenceEnabled() &&
                (await this.deviceIntelligenceContext.buildDeviceIntelligenceHeader({
                    skipMangopay: true,
                    skipIncognia: true,
                    skipForter: true,
                }))),
        };
        const isOnline = navigator.onLine;

        return this.measureP<SignInViewModel>(response => ({
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            success: response?.success ?? false,
            type: signupRequest.credentials.authType,
            endpoint: 'signup',
        }))(
            this.ulClient.signup(signupRequest, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('Signup', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return toSignupViewModel(wrappedResponse.data);
            })
        );
    };

    public deleteAccount = (deleteRequest?: {}) => {
        const headers = this.buildUniversalLoginHeader();
        const isOnline = navigator.onLine;
        return this.measureP<DeleteAccountViewModel>(response => ({
            success: response?.success ?? false,
            responseCode: response ? toGdprResponseCode(response.status) : 'err',
            endpoint: 'deleteAccount',
        }))(
            this.ulClient.deleteAccount(deleteRequest, headers).then(wrappedResponse => {
                if (!wrappedResponse.data || wrappedResponse.data.status === GdprEraseStatus.Error) {
                    const { statusCode, error } = wrappedResponse;
                    if (!!wrappedResponse.error) {
                        this.measureError('deleteAccount', statusCode);
                    }
                    this.logger.error(
                        `deleteAccount request failed - status=${wrappedResponse.data?.status}; error=${error}`
                    );
                    return {
                        success: false,
                        status: GdprEraseStatus.Error,
                    };
                }

                return toDeleteAccountViewModel(wrappedResponse.data);
            })
        );
    };

    public forceLogout = () => {
        const headers = this.buildUniversalLoginHeader();
        const isOnline = navigator.onLine;
        return this.measureP<BaseViewModel>(response => ({
            success: response?.success ?? false,
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            endpoint: 'forceLogout',
        }))(
            this.ulClient.forceLogout(undefined, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('forceLogout', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return toForceLogoutViewModel(wrappedResponse.data);
            })
        );
    };

    public getChannels = (transactionId: string) => {
        const headers = {
            ...this.buildUniversalLoginHeader(),
            ...this.getMfaTransactionHeader(transactionId),
        };
        const isOnline = navigator.onLine;
        return this.measureP<OtpChannelsViewModel>(response => ({
            success: response?.success ?? false,
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            endpoint: 'getChannels',
        }))(
            this.ulClient.getChannels(headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('getChannels', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return toOtpChannelsViewModel(wrappedResponse.data);
            })
        );
    };

    public generateOtp = (sendOtpRequest: SendOtpRequest, transactionId: string) => {
        const headers = {
            ...this.buildUniversalLoginHeader(),
            ...this.getMfaTransactionHeader(transactionId),
        };
        const isOnline = navigator.onLine;
        return this.measureP<OtpGenerateViewModel>(response => ({
            success: response?.success ?? false,
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            endpoint: 'generateOtp',
        }))(
            this.ulClient.generateOtp(sendOtpRequest, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('generateOtp', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return toOtpGenerateViewModel(wrappedResponse.data);
            })
        );
    };

    public verifyOtp = (verifyOtpRequest: VerifyOtpRequest, transactionId: string) => {
        const headers = {
            ...this.buildUniversalLoginHeader(),
            ...this.getMfaTransactionHeader(transactionId),
        };
        const isOnline = navigator.onLine;
        return this.measureP<OtpVerifyViewModel>(response => ({
            success: response?.success ?? false,
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            endpoint: 'verifyOtp',
        }))(
            this.ulClient.verifyOtp(verifyOtpRequest, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('verifyOtp', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return toOtpVerifyViewModel(wrappedResponse.data);
            })
        );
    };

    public sendAuthedOtp = async (sendAuthedOtpRequest?: SendAuthedOtpRequest) => {
        const headers = {
            ...this.buildUniversalLoginHeader(),
            ...(this.deviceIntelligenceContext.isDeviceIntelligenceEnabled() &&
                (await this.deviceIntelligenceContext.buildDeviceIntelligenceHeader({
                    skipBureau: true,
                    skipMangopay: true,
                    skipIncognia: true,
                    skipForter: true,
                    skipFingerprint: true,
                }))),
        };
        const isOnline = navigator.onLine;
        return this.measureP<OtpGenerateViewModel>(response => ({
            success: response?.success ?? false,
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            endpoint: 'sendAuthedOtp',
        }))(
            this.ulClient.sendAuthedOtp(sendAuthedOtpRequest, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('sendAuthedOtp', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return toOtpGenerateViewModel(wrappedResponse.data);
            })
        );
    };

    public verifyOtpV2 = (verifyOtpRequest: VerifyOtpRequest) => {
        const headers = {
            ...this.buildUniversalLoginHeader(),
        };
        const isOnline = navigator.onLine;
        return this.measureP<OtpVerifyViewModel>(response => ({
            success: response?.success ?? false,
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            endpoint: 'verifyOtpV2',
        }))(
            this.ulClient.verifyOtpV2(verifyOtpRequest, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('verifyOtpV2', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return toOtpVerifyViewModel(wrappedResponse.data);
            })
        );
    };

    public verifyAuthedOtp = async (verifyOtpRequest: VerifyOtpRequest) => {
        const headers = {
            ...this.buildUniversalLoginHeader(),
            ...(this.deviceIntelligenceContext.isDeviceIntelligenceEnabled() &&
                (await this.deviceIntelligenceContext.buildDeviceIntelligenceHeader({
                    skipMangopay: true,
                    skipIncognia: true,
                    skipForter: true,
                    skipBureau: true,
                    skipFingerprint: true,
                }))),
        };
        const isOnline = navigator.onLine;
        return this.measureP<OtpVerifyViewModel>(response => ({
            success: response?.success ?? false,
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            endpoint: 'verifyAuthed',
        }))(
            this.ulClient.verifyAuthedOtp(verifyOtpRequest, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('verifyAuthedOtp', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return toOtpVerifyViewModel(wrappedResponse.data);
            })
        );
    };

    public initializePhoneAssociation = () => {
        const headers = {
            ...this.buildUniversalLoginHeader(),
        };
        const isOnline = navigator.onLine;
        return this.measureP<PhoneAssociationViewModel>(response => ({
            success: response?.success ?? false,
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            endpoint: 'initializePhoneAssociation',
        }))(
            this.ulClient.initializePhoneAssociation(headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('initializePhoneAssociation', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return toPhoneAsscociationViewModel(wrappedResponse.data);
            })
        );
    };

    public verifyPhoneAssociation = (verifyPhoneAssociationRequest: PhoneAssociationRequest) => {
        const headers = {
            ...this.buildUniversalLoginHeader(),
        };
        const isOnline = navigator.onLine;
        return this.measureP<PhoneAssociationViewModel>(response => ({
            success: response?.success ?? false,
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            endpoint: 'verifyPhoneAssociation',
        }))(
            this.ulClient.verifyPhoneAssociation(verifyPhoneAssociationRequest, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('verifyPhoneAssociation', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return toPhoneAsscociationViewModel(wrappedResponse.data);
            })
        );
    };

    public verifyWhatsappLink = (verifyWhatsappLinkRequest: WhatsappLinkRequest) => {
        const headers = {
            ...this.buildUniversalLoginHeader(),
        };
        const isOnline = navigator.onLine;
        return this.measureP<PhoneAssociationViewModel>(response => ({
            success: response?.success ?? false,
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            endpoint: 'verifyWhatsappLink',
        }))(
            this.ulClient.verifyWhatsappLink(verifyWhatsappLinkRequest, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('verifyWhatsappLink', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return toPhoneAsscociationViewModel(wrappedResponse.data);
            })
        );
    };

    public associateSocial = (socialType: string, request: SocialAssociationRequest) => {
        const headers = {
            ...this.buildUniversalLoginHeader(),
        };
        const isOnline = navigator.onLine;

        this.logger.info('associateSocial: Initiating API call', undefined, {
            event: 'associate_social_api_start',
            socialType,
            hasCode: !!request?.credentials?.code ? 'true' : 'false',
            hasAccessToken: !!request?.credentials?.accessToken ? 'true' : 'false',
            hasIdToken: !!request?.credentials?.idToken ? 'true' : 'false',
            credentialId: request?.credentials?.id,
            requestId: headers[ApiHeader.RequestId],
        });

        return this.measureP<SocialAssociationViewModel>(response => ({
            success: response?.success ?? false,
            endpoint: 'associateSocial',
            statusCode: response?.statusCode,
        }))(
            this.ulClient
                .associateSocial(socialType, request, headers)
                .then(wrappedResponse => {
                    if (!!wrappedResponse.error) {
                        const { statusCode, error } = wrappedResponse;
                        this.logAndMeasureError('associateSocial', statusCode, error, isOnline);
                    }

                    // If HTTP status is not 200 or no data, return internal error
                    if (wrappedResponse.statusCode !== 200 || !wrappedResponse.data) {
                        this.logger.error('associateSocial: Non-200 response or missing data', undefined, {
                            event: 'associate_social_api_non_200',
                            statusCode: String(wrappedResponse.statusCode),
                            hasError: !!wrappedResponse.error ? 'true' : 'false',
                            hasData: !!wrappedResponse.data ? 'true' : 'false',
                            errorMessage: wrappedResponse.error?.message || 'unknown',
                        });

                        return {
                            success: false,
                            statusCode: 299,
                            message: 'Internal error',
                        };
                    }

                    return toSocialAssociationViewModel(wrappedResponse.data);
                })
                .catch(error => {
                    this.logger.error('associateSocial: API call exception', error, {
                        event: 'associate_social_api_exception',
                        errorType: error?.constructor?.name || 'unknown',
                        errorMessage: error?.message || 'unknown',
                        isNetworkError:
                            error?.name === 'NetworkError' || error?.message?.includes('network') ? 'true' : 'false',
                        isTimeoutError: error?.message?.includes('timeout') ? 'true' : 'false',
                        statusCode: '-1',
                    });

                    this.logAndMeasureError('associateSocial', -1, error, isOnline);
                    return {
                        success: false,
                        statusCode: 299,
                        message: 'Internal error',
                    };
                })
        );
    };

    public validateToken = (token: string) => {
        const headers = {
            ...this.buildUniversalLoginHeader(),
            ...this.getTokenHeader(token),
        };
        const isOnline = navigator.onLine;
        return this.measureP<ValidateTokenViewModel>(response => ({
            success: response?.success ?? false,
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            endpoint: 'validateToken',
        }))(
            this.ulClient.validateToken({}, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('validateToken', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return wrappedResponse.data;
            })
        );
    };

    public signInCallback = (loginFlow: LoginFlow = LoginFlow.UnifiedAuth) => {
        const headers = {
            ...this.buildUniversalLoginHeader(),
        };
        const isOnline = navigator.onLine;
        return this.measureP<SignInCallbackViewModel>(response => ({
            success: response?.success ?? false,
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            endpoint: 'signInCallback',
        }))(
            this.ulClient.signInCallback({ loginFlow }, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('signInCallback', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return wrappedResponse.data;
            })
        );
    };

    public refreshToken = () => {
        const headers = this.buildUniversalLoginHeader();
        const isOnline = navigator.onLine;
        return this.measureP<RefreshTokenViewModel>(response => ({
            success: response?.success ?? false,
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            endpoint: 'refreshToken',
        }))(
            this.ulClient.refreshToken(undefined, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('refreshToken', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return toRefreshTokenViewModel(wrappedResponse.data);
            })
        );
    };

    private getTokenHeader = (token: string) => {
        return {
            [ApiHeader.Token]: `Bearer ${token}`,
        };
    };

    private getMfaTransactionHeader = (transactionId: string) => {
        return {
            [ApiHeader.TransactionId]: transactionId,
        };
    };

    private buildUniversalLoginHeader = () => {
        const ulHeaders = {
            [ApiHeader.AppId]: this.appContext.appId,
            [ApiHeader.FallbackOrigin]: window.location.origin,
            [ApiHeader.RequestId]: this.appContext.correlationId, // correlationId from Cronos identifies an UL sessions, and will be used in backend logs to group requests
            [ApiHeader.AppVersion]: this.appContext.appVersion,
        };

        if (__DEV__ || !!__ULAPI__) {
            return { ...this.buildAgHeader(), ...ulHeaders };
        }

        return { ...ulHeaders, ...this.buildCdnHeader() };
    };

    private buildCdnHeader = () => {
        if (this.appContext.isCnTunnel) {
            return {
                [CdnHeader.CnTunnel]: 'true',
            };
        }
        return {};
    };

    private mapErrorResponseCode = (statusCode: number): ResponseCode => {
        switch (statusCode) {
            case FetchHandlerStatusCode.TimeoutError:
            case 408:
                return ResponseCode.RequestTimeout;
            case FetchHandlerStatusCode.FetchError:
                return ResponseCode.NetworkError;
            case FetchHandlerStatusCode.JsonParseError:
                return ResponseCode.ClientBadRequest;
            case 404:
                return ResponseCode.ClientNotFound;
            default:
                if (statusCode >= 400 && statusCode < 500) {
                    return ResponseCode.ClientBadRequest;
                }
                return ResponseCode.ErrorOccurred;
        }
    };

    private logAndMeasureError = (requestName: string, statusCode: number, error?: Error, isOnline: boolean = true) => {
        this.logger.error(`${requestName} request failed; ${statusCode}: ${error}; isOnline: ${isOnline}`, error);
        this.measureError(requestName, statusCode, isOnline);
    };

    private measureError = (requestName: string, statusCode: number, isOnline: boolean = true) => {
        const measurementTags = {
            requestName,
            statusCode,
            isOnline,
            appId: this.appContext.appId || '',
            pathname: window.location.pathname,
        };
        this.measurement.count('ul.spa.networkerror', 1, measurementTags);
    };

    public unifiedAuth = (authRequest: UnifiedAuthRequest): Promise<UnifiedAuthViewModel> => {
        const baseHeaders = this.buildUniversalLoginHeader();
        const isOnline = navigator.onLine;
        if (!this.deviceIntelligenceContext.isDeviceIntelligenceHeaderBuilt()) {
            this.deviceIntelligenceContext.buildDeviceIntelligenceHeaderAsync();
        }
        const shouldSendDeviceIntelligenceHeaders =
            this.deviceIntelligenceContext.isDeviceIntelligenceHeaderBuilt() &&
            !this.isDeviceIntelligenceHeadersSent &&
            Object.keys(this.deviceIntelligenceContext.getDeviceIntelligenceHeaders()).length > 0;
        const headers = shouldSendDeviceIntelligenceHeaders
            ? { ...baseHeaders, ...this.deviceIntelligenceContext.getDeviceIntelligenceHeaders() }
            : baseHeaders;

        if (shouldSendDeviceIntelligenceHeaders) {
            this.isDeviceIntelligenceHeadersSent = true;
        }

        return this.measureP<SignInViewModel>(response => ({
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            success: response?.success ?? false,
            endpoint: 'auth',
            rawResponseCode: response.responseCode.toLocaleString(),
        }))(
            this.ulClient.authLogin(authRequest, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('UnifiedAuth', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return toUnifiedAuthViewModel(wrappedResponse.data);
            })
        );
    };

    public signUpConsent(ConsentInfoRequest: ConsentInfoRequest): Promise<ConsentInfoResponse> {
        const headers = this.buildUniversalLoginHeader();
        const isOnline = navigator.onLine;

        return this.measureP<ConsentInfoResponse>(response => ({
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            success: response?.success ?? false,
            endpoint: 'signUpConsent',
        }))(
            this.ulClient.signUpConsent(ConsentInfoRequest, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('SignUpConsent', statusCode, error, isOnline);
                }
                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                        downStreamServiceFailure: false,
                    };
                }
                return wrappedResponse.data as ConsentInfoResponse;
            })
        );
    }

    public resetPin = (walletRequest: WalletResetPinRequest): Promise<WalletViewModel> => {
        const headers = this.buildUniversalLoginHeader();
        const isOnline = navigator.onLine;

        return this.measureP<WalletViewModel>(response => ({
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            success: response?.success ?? false,
            endpoint: 'resetPin',
        }))(
            this.ulClient.resetPin(walletRequest, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('ResetPin', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return toWalletViewModel(wrappedResponse.data);
            })
        );
    };

    public walletAuth = (walletRequest?: WalletAuthRequest, transactionId?: string): Promise<WalletViewModel> => {
        const headers = {
            ...this.buildUniversalLoginHeader(),
            ...(transactionId && this.getMfaTransactionHeader(transactionId)),
        };
        const isOnline = navigator.onLine;

        return this.measureP<WalletViewModel>(response => ({
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            success: response?.success ?? false,
            endpoint: 'walletAuth',
        }))(
            this.ulClient.walletAuth(walletRequest, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('PinAuth', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return toWalletViewModel(wrappedResponse.data);
            })
        );
    };

    public scaAuthen = (walletRequest?: WalletScaRequest, transactionId?: string): Promise<WalletViewModel> => {
        const headers = {
            ...this.buildUniversalLoginHeader(),
            ...(transactionId && this.getMfaTransactionHeader(transactionId)),
        };
        const isOnline = navigator.onLine;

        return this.measureP<WalletViewModel>(response => ({
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            success: response?.success ?? false,
            endpoint: 'scaAuthen',
        }))(
            this.ulClient.scaAuthen(walletRequest, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('ScaAuthen', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return toWalletViewModel(wrappedResponse.data);
            })
        );
    };

    public scaInit = (request?: ScaInitRequest): Promise<ScaInitViewModel> => {
        const headers = this.buildUniversalLoginHeader();
        const isOnline = navigator.onLine;

        return this.measureP<ScaInitViewModel>(response => ({
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            success: response?.success ?? false,
            endpoint: 'scaInit',
        }))(
            this.ulClient.scaInit(request, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('ScaInit', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                const { success, responseCode, passkey, downStreamServiceFailure } = wrappedResponse.data;
                return {
                    success,
                    responseCode,
                    passkey,
                    downStreamServiceFailure,
                };
            })
        );
    };

    public getPasskeyRegistrationOptions = async (): Promise<PasskeyRegistrationOptionsResponse> => {
        const headers = this.buildUniversalLoginHeader();
        const isOnline = navigator.onLine;

        return this.measureP<PasskeyRegistrationOptionsResponse>(response => ({
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            success: response?.success ?? false,
            endpoint: 'passkey/register/options',
        }))(
            this.ulClient.getPasskeyRegistrationOptions(undefined, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('PasskeGetRegistrationOptions', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return wrappedResponse.data;
            })
        );
    };

    public registerPasskey = async (request: RegisterPasskeyRequest): Promise<RegisterPasskeyResponseViewModel> => {
        const headers = this.buildUniversalLoginHeader();
        const isOnline = navigator.onLine;

        return this.measureP<RegisterPasskeyResponseViewModel>(response => ({
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            success: response?.success ?? false,
            endpoint: 'passkey/register/verify',
        }))(
            this.ulClient.registerPasskey(request, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('PasskeyVerifyRegistration', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return wrappedResponse.data;
            })
        );
    };

    public getPasskeyAuthenticationOptions = async (): Promise<PasskeyAuthenticationOptionsResponse> => {
        const headers = this.buildUniversalLoginHeader();
        const isOnline = navigator.onLine;

        return this.measureP<PasskeyAuthenticationOptionsResponse>(response => ({
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            success: response?.success ?? false,
            endpoint: 'passkey/authenticate/options',
        }))(
            this.ulClient.getPasskeyAuthenticationOptions(undefined, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('PasskeyGetAuthenticationOptions', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                    };
                }

                return wrappedResponse.data;
            })
        );
    };

    public authenticateWithPasskey = async (
        request: AuthenticateWithPasskeyRequest
    ): Promise<PasskeyAuthenticateResponse> => {
        const headers = {
            ...this.buildUniversalLoginHeader(),
            ...(request.transactionId && this.getMfaTransactionHeader(request.transactionId)),
        };
        const isOnline = navigator.onLine;

        return this.measureP<PasskeyAuthenticateResponse>(response => ({
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            success: response?.success ?? false,
            endpoint: 'passkey/authenticate/verify',
        }))(
            this.ulClient.authenticateWithPasskey(request, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('PasskeyVerifyAuthentication', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                        downStreamServiceFailure: true,
                    };
                }

                return wrappedResponse.data;
            })
        );
    };

    public getUserPasskeys = async () => {
        const headers = this.buildUniversalLoginHeader();
        const isOnline = navigator.onLine;

        return this.measureP<UserPasskeysViewModel>(response => ({
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            success: response?.success ?? false,
            endpoint: 'passkey/list',
        }))(
            this.ulClient.getUserPasskeys(headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('UserPasskeys', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                        downStreamServiceFailure: true,
                    };
                }

                return toUserPasskeysViewModel(wrappedResponse.data);
            })
        );
    };

    public updatePasskeyFriendlyName = (
        updateRequest: UserPasskeyUpdateFriendlyNameRequest
    ): Promise<UserPasskeyViewModel> => {
        const headers = this.buildUniversalLoginHeader();
        const isOnline = navigator.onLine;

        return this.measureP<UserPasskeyViewModel>(response => ({
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            success: response?.success ?? false,
            endpoint: 'passkey/update',
        }))(
            this.ulClient.updatePasskeyFriendlyName(updateRequest, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('UpdatePasskeyFriendlyName', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                        downStreamServiceFailure: true,
                    };
                }

                return toUserPasskeyViewModel(wrappedResponse.data);
            })
        );
    };

    public deleteUserPasskey = async (deletePasskeyRequest: UserPasskeyDeleteRequest): Promise<BaseViewModel> => {
        const headers = this.buildAgHeader();
        const isOnline = navigator.onLine;

        return this.measureP<BaseViewModel>(response => ({
            responseCode: response ? ResponseCode[response.responseCode] : 'err',
            success: response?.success ?? false,
            endpoint: 'passkey/update',
        }))(
            this.ulClient.deleteUserPasskey(deletePasskeyRequest, headers).then(wrappedResponse => {
                if (!!wrappedResponse.error) {
                    const { statusCode, error } = wrappedResponse;
                    this.logAndMeasureError('DeleteUserPasskey', statusCode, error, isOnline);
                }

                if (!wrappedResponse.data) {
                    return {
                        success: false,
                        responseCode: this.mapErrorResponseCode(wrappedResponse.statusCode),
                        downStreamServiceFailure: true,
                    };
                }

                return toBaseViewModel(wrappedResponse.data);
            })
        );
    };
}
